import { create } from "zustand";
import { OrganizationDto } from "../../../models/organization/dto/organization.dto";
import { createSelectors } from "../../../utils/store";

type State = {
    selectedOrganization?: OrganizationDto;
    setSelectedOrganization: (newOrganization?: OrganizationDto | null) => void;
}

/**
 * Merge an incoming organization into the stored one, keeping fields that are
 * scoped to the CALLER rather than to the organization.
 *
 * `OrganizationDto.myRole` is the caller's role in that org, and only the two
 * caller-scoped endpoints populate it (`GET my-organizations`,
 * `GET get-current-organization`). Every org-settings mutation returns the same
 * DTO *without* it, and ~8 of those hooks store the response wholesale
 * (`setSelectedOrganization(data)`). Assigning that response directly would
 * drop `myRole` back to `undefined`, and `resolveUserRole` would silently fall
 * back to the global `role` claim mid-session.
 *
 * That is not theoretical: a user who is ADMIN in the selected org but whose
 * global claim says `coach` (they coach in another org — the exact case
 * per-org roles exist to fix) would have their dashboard collapse to the three
 * coach sections the moment they saved a setting, and the layout catch-all
 * would bounce them off the settings page they were on.
 *
 * Rules:
 * - `undefined` incoming `myRole` means "this endpoint does not know" → keep
 *   what we had.
 * - `null` is a real answer ("no membership row", i.e. an elevated/platform
 *   admin) and overwrites, so it must not be conflated with `undefined`.
 * - Only preserve within the SAME org id. Switching orgs must never carry a
 *   role across a tenant boundary.
 */
export function mergeSelectedOrganization(
    current: OrganizationDto | undefined,
    next?: OrganizationDto | null,
): OrganizationDto | undefined {
    if (!next) return undefined;
    if (next.myRole !== undefined) return next;
    if (!current || current.id !== next.id) return next;
    return { ...next, myRole: current.myRole };
}

const store = create<State>((set, get) => {
    return {
        selectedOrganization: undefined,
        setSelectedOrganization: (selectedOrganization?: OrganizationDto | null) =>
            set((state) => ({
                selectedOrganization: mergeSelectedOrganization(
                    state.selectedOrganization,
                    selectedOrganization,
                ),
            })),
    }
})
export const useSelectedOrganization = createSelectors(store);
